import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as CloseIcon } from '@repo/common/assets/icons/24px/icon_24px_cancel.svg';
import { ReactComponent as FilterIcon } from '@repo/common/assets/icons/20px/icon_20px_filter.svg';

import {
  applyTypography,
  ToggleButton,
  BaseButton,
  Chip,
  DropDown,
  FilterBottomSheet,
  ToolTip,
  Checkbox,
  isAnyCareerLevel,
  matchesLocationSelection,
  matchesVisaSelection,
  useAuth,
  useT,
  CloseButton,
} from '@repo/common';

import { getJobBoardCategories } from '../../api/jobboard/jobboard.api';

/**
 * 모바일 필터 칩 색상.
 */
const CHIP_COLORS = {
  default: {
    chip: 'bg-grayscale-gray02 ring-grayscale-gray02 text-text-secondary',
    count: 'text-text-placeholder',
    icon: 'text-icon-tertiary',
  },
  selected: {
    chip: 'bg-green-light01 ring-green-light01 text-green-deep04',
    count: 'text-green-deep03',
    icon: 'text-green-deep04',
  },
};

const E7_CATEGORY_ID = 1;
const CAREER_LEVEL_CATEGORY_ID = 5;
const NON_ADMIN_VISIBLE_STATUSES = new Set(['ACTIVE', 'CLOSED']);

const normalizeLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const normalizeSelectionMap = (filters = {}) => {
  const normalized = {};

  Object.entries(filters).forEach(([key, values]) => {
    normalized[key] = Array.isArray(values) ? [...values].sort() : [];
  });

  return normalized;
};

const areSelectionMapsEqual = (left = {}, right = {}) => {
  const leftMap = normalizeSelectionMap(left);
  const rightMap = normalizeSelectionMap(right);
  const keys = new Set([...Object.keys(leftMap), ...Object.keys(rightMap)]);

  return [...keys].every((key) => {
    const leftValues = leftMap[key] || [];
    const rightValues = rightMap[key] || [];

    if (leftValues.length !== rightValues.length) {
      return false;
    }

    return leftValues.every((value, index) => value === rightValues[index]);
  });
};

const listIncludesLabel = (list, label) => {
  const target = normalizeLabel(label);
  if (!Array.isArray(list) || !target) return false;

  return list.some((item) => {
    const normalizedItem = normalizeLabel(item);
    return normalizedItem.includes(target) || target.includes(normalizedItem);
  });
};

const matchesFilterOption = (job, categoryId, optionName) => {
  switch (categoryId) {
    case E7_CATEGORY_ID:
      return Boolean(job?.hasE7Support);
    case 2:
      return matchesLocationSelection(job, optionName);
    case 3:
      return listIncludesLabel(job?.requiredLanguageLabels, optionName);
    case 4:
      return listIncludesLabel([job?.role], optionName);
    case 5:
      // 조건이 없는 항목이라 모든 공고가 해당된다
      if (isAnyCareerLevel(optionName)) return true;

      // 경력 미지정 공고 = 경력 무관. 목록 필터와 같은 규칙으로 센다
      return (
        !String(job?.careerLevel ?? '').trim() ||
        listIncludesLabel([job?.careerLevel], optionName)
      );
    case 6:
      return matchesVisaSelection(job, optionName);
    case 7:
      return listIncludesLabel([job?.employmentType], optionName);
    default:
      return false;
  }
};

/**
 * @param {boolean} [collapsible] - 데스크탑 공고 관리: 접이식 패널
 * @param {boolean} [management] - 공고 관리 화면(모바일 칩 바 포함) — 상태와 무관하게 전체 공고로 개수를 센다
 */
const FilterSidebar = ({ onFilterChange, className = '', jobs = [], collapsible = false, management = false }) => {
  const t = useT();
  const { user } = useAuth();
  const userRole = user?.role ?? null;
  const isAdmin = userRole === 'A';
  const [expanded, setExpanded] = useState(false);

  const [e7Support, setE7Support] = useState(false);
  const [openFilters, setOpenFilters] = useState({});
  const [mobileSheet, setMobileSheet] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [appliedE7Support, setAppliedE7Support] = useState(false);

  const hasDraftFilterSelected =
    Object.values(selectedFilters).some((arr) => arr.length > 0) || e7Support;
  const hasAppliedFilterSelected =
    Object.values(appliedFilters).some((arr) => arr.length > 0) ||
    appliedE7Support;
  const hasPendingChanges =
    e7Support !== appliedE7Support ||
    !areSelectionMapsEqual(selectedFilters, appliedFilters);

  const filterItems = categories
    .filter((category) => category.id !== E7_CATEGORY_ID)
    .map((category) => ({
      key: category.id,
      id: category.id,
      label: category.name,
      items:
        category.id === CAREER_LEVEL_CATEGORY_ID
          ? (category.items || []).filter(
              (item) => !normalizeLabel(item?.name).includes('entry'),
            )
          : category.items || [],
    }));

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getJobBoardCategories();
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    setSelectedFilters((prev) => {
      const next = { ...prev };
      categories.forEach((category) => {
        if (!Array.isArray(next[category.id])) {
          next[category.id] = [];
        }
      });
      return next;
    });
  }, [categories]);

  const e7Category = categories.find(
    (category) => category.id === E7_CATEGORY_ID,
  );

  const countSourceJobs = useMemo(
    () =>
      isAdmin || collapsible || management
        ? jobs
        : jobs.filter((job) => NON_ADMIN_VISIBLE_STATUSES.has(job.status)),
    [isAdmin, collapsible, management, jobs],
  );

  const optionCounts = useMemo(() => {
    const counts = {};

    categories.forEach((category) => {
      (category.items || []).forEach((item) => {
        const key = `${category.id}:${item.name}`;
        counts[key] = countSourceJobs.filter((job) =>
          matchesFilterOption(job, category.id, item.name),
        ).length;
      });
    });

    return counts;
  }, [categories, countSourceJobs]);

  const toggleFilter = (filterKey) => {
    setOpenFilters((prev) => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }));
  };

  const handleE7Toggle = () => {
    const nextValue = !e7Support;
    setE7Support(nextValue);
    setSelectedFilters((prev) => ({
      ...prev,
      [E7_CATEGORY_ID]: nextValue ? ['E-7 Visa Support'] : [],
    }));
  };

  const handleFilterToggle = (categoryId, optionName) => {
    setSelectedFilters((prev) => {
      const prevList = prev[categoryId] || [];
      const newList = prevList.includes(optionName)
        ? prevList.filter((value) => value !== optionName)
        : [...prevList, optionName];
      return { ...prev, [categoryId]: newList };
    });
  };

  const commitFilters = (nextSelected, nextE7Support) => {
    const nextApplied = {
      ...nextSelected,
      [E7_CATEGORY_ID]: nextE7Support ? ['E-7 Visa Support'] : [],
    };

    setAppliedFilters(nextApplied);
    setAppliedE7Support(nextE7Support);
    onFilterChange?.({ ...nextApplied, e7Support: nextE7Support });
  };

  const handleApplyFilters = () => {
    commitFilters(selectedFilters, e7Support);
  };

  const handleE7ChipToggle = () => {
    const nextValue = !e7Support;
    const nextSelected = {
      ...selectedFilters,
      [E7_CATEGORY_ID]: nextValue ? ['E-7 Visa Support'] : [],
    };

    setE7Support(nextValue);
    setSelectedFilters(nextSelected);
    commitFilters(nextSelected, nextValue);
  };

  const handleClearAll = () => {
    const cleared = Object.keys(selectedFilters).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});

    setSelectedFilters(cleared);
    setE7Support(false);
    setAppliedFilters(cleared);
    setAppliedE7Support(false);

    onFilterChange?.({
      ...cleared,
      [E7_CATEGORY_ID]: [],
      e7Support: false,
    });
  };

  const FilterOptionRow = ({
    label,
    count,
    description,
    checked,
    onToggle,
  }) => {
    return (
      <div className='flex items-center w-full px-0 py-[6px] gap-2'>
        <div className='flex flex-col flex-1 w-full'>
          <div className='flex items-baseline gap-0.5'>
            <p
              className={`${applyTypography(
                'b14',
              )} text-text-tertiary break-all`}
            >
              {label}
            </p>
            <p
              className={`${applyTypography(
                'b12',
              )} text-text-placeholder shrink-0`}
            >
              ({count})
            </p>
          </div>

          {description && (
            <p
              className={`${applyTypography(
                'b12',
              )} text-text-placeholder leading-snug`}
            >
              {description}
            </p>
          )}
        </div>

        <div className='flex items-center shrink-0'>
          <Checkbox checked={checked} onChange={onToggle} />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!mobileSheet) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileSheet]);

  const appliedCountOf = (categoryId) =>
    selectedFilters[categoryId]?.length || 0;

  const totalAppliedCount = Object.values(appliedFilters).reduce(
    (acc, list) => acc + (list?.length || 0),
    0,
  );

  const e7SupportRow = (
    <div className='flex items-center justify-between relative shrink-0 w-full'>
      <div className='flex gap-1 items-center relative shrink-0'>
        <p className='typo-b16Strong relative shrink-0 text-green-deep03'>
          {e7Category?.name}
        </p>
        <div className='flex flex-row items-center self-stretch'>
          <div className='flex flex-col h-full items-start justify-center relative shrink-0'>
            <p className='typo-b12 relative shrink-0 text-text-placeholder w-full whitespace-pre-wrap'>
              ({optionCounts[`${E7_CATEGORY_ID}:E-7 Visa Support`] || 0})
            </p>
          </div>
        </div>
      </div>
      <ToggleButton
        onChange={handleE7Toggle}
        value={e7Support}
        offBgColor='bg-grayscale-gray03'
        offCircleColor='bg-grayscale-gray00'
      />
    </div>
  );

  const renderOptionList = (item) => (
    <div className='flex flex-col items-start w-full'>
      {(item.items || []).map((option) => (
        <FilterOptionRow
          key={option.id}
          label={option.name}
          description={option.description}
          count={optionCounts[`${item.id}:${option.name}`] || 0}
          checked={selectedFilters[item.id]?.includes(option.name)}
          onToggle={() => handleFilterToggle(item.id, option.name)}
        />
      ))}
    </div>
  );

  const renderAccordion = (item) => {
    const isOpen = openFilters[item.key] || false;

    return (
      <div
        key={item.key}
        className='border-b border-grayscale-gray02 border-solid flex flex-col items-start px-0 py-3 relative shrink-0 w-full'
      >
        <button
          type='button'
          onClick={() => toggleFilter(item.key)}
          className={`flex items-center justify-center relative shrink-0 w-full ${
            isOpen ? 'pb-3' : 'pb-0'
          }`}
        >
          <div className='flex flex-[1_0_0] items-center min-h-px relative '>
            <p className='typo-b16Strong relative shrink-0 text-text-primary flex items-center gap-2'>
              {item.label}
              {appliedCountOf(item.key) > 0 && (
                <span className='typo-b14 text-text-success'>
                  {appliedCountOf(item.key)}
                </span>
              )}
            </p>
          </div>
          <div className='flex flex-row items-center self-stretch'>
            <div className='flex h-full items-center py-0 relative shrink-0'>
              <div className='flex items-center justify-center w-[22px] h-6'>
                {isOpen ? (
                  <ArrowTopIcon className='text-icon-primary' />
                ) : (
                  <ArrowBottomIcon className='text-icon-primary' />
                )}
              </div>
            </div>
          </div>
        </button>

        {isOpen && item.items && renderOptionList(item)}
      </div>
    );
  };

  const actionButtons = (
    <>
      <BaseButton
        hideIcon
        isDisabled={!hasPendingChanges}
        width='100%'
        onClick={handleApplyFilters}
      >
        {t('Apply filters', '필터 적용')}
      </BaseButton>
      <BaseButton
        type='text'
        hideIcon
        isDisabled={!hasDraftFilterSelected && !hasAppliedFilterSelected}
        width='100%'
        onClick={handleClearAll}
      >
        {t('Clear all', '전체 해제')}
      </BaseButton>
    </>
  );

  const activeSheetItem =
    mobileSheet && mobileSheet !== 'all'
      ? filterItems.find((item) => String(item.key) === String(mobileSheet))
      : null;

  /* ── 모바일: 칩 바 ── */
  const mobileChipBar = (
    <div className='flex w-full items-center gap-2 tabletV:hidden'>
      <button
        type='button'
        aria-label={t('Open filters', '필터 열기')}
        onClick={() => setMobileSheet('all')}
        className={`
          flex h-9 shrink-0 items-center gap-1.5 rounded-full border
          bg-grayscale-gray00 transition-colors
          ${totalAppliedCount > 0 ? 'border-grayscale-gray09 pl-2.5 pr-1.5' : 'w-9 justify-center border-grayscale-gray02'}
        `}
      >
        <FilterIcon className='shrink-0 text-icon-primary' />
        {totalAppliedCount > 0 && (
          <span className='flex h-5 min-w-[20px] items-center justify-center rounded-full bg-grayscale-gray09 px-1.5 typo-b11Strong text-text-inverse'>
            {totalAppliedCount}
          </span>
        )}
      </button>

      <div className='flex min-w-0 flex-1 items-center gap-2 overflow-x-auto scrollbar-hide'>
        {/* E-7 Support 토글 칩 */}
        <Chip
          size='l'
          type='fill'
          hideIcon
          disableDefaultColors
          onClick={handleE7ChipToggle}
          bgclass={`shrink-0 h-9 ${
            e7Support ? CHIP_COLORS.selected.chip : CHIP_COLORS.default.chip
          }`}
        >
          <span className='flex items-center gap-1'>
            {e7Category?.name || 'E-7 Support'}
            <span
              className={`typo-b14 ${
                e7Support
                  ? CHIP_COLORS.selected.count
                  : CHIP_COLORS.default.count
              }`}
            >
              ({optionCounts[`${E7_CATEGORY_ID}:E-7 Visa Support`] || 0})
            </span>
          </span>
        </Chip>

        {filterItems.map((item) => {
          const count = appliedCountOf(item.key);
          const isSelected = count > 0;

          return (
            <DropDown
              key={item.key}
              size='s'
              width='fit'
              hideMenu
              open={mobileSheet === item.key}
              onOpenChange={(next) => setMobileSheet(next ? item.key : null)}
              className='shrink-0'
              fieldClassName={
                isSelected
                  ? 'border-green-light01 bg-green-light01'
                  : 'border-grayscale-gray02'
              }
              placeholder={
                <span className='flex items-center gap-1 whitespace-nowrap'>
                  {/* 미선택 상태는 DropDown 기본 placeholder 색을 그대로 사용 */}
                  <span className={isSelected ? 'text-green-deep04' : ''}>
                    {item.label}
                  </span>
                  {isSelected && (
                    <span className='typo-b13 text-green-deep03'>
                      ({count})
                    </span>
                  )}
                </span>
              }
            />
          );
        })}
      </div>
    </div>
  );

  const mobileFooterButtons = (
    <div className='flex w-full shrink-0 items-center gap-2'>
      <BaseButton
        type='text'
        size='l'
        hideIcon
        isDisabled={!hasDraftFilterSelected && !hasAppliedFilterSelected}
        bgclass='shrink-0 bg-transparent px-3'
        onClick={handleClearAll}
      >
        {t('Clear all', '전체 해제')}
      </BaseButton>
      <BaseButton
        size='l'
        hideIcon
        width='100%'
        isDisabled={!hasPendingChanges}
        onClick={() => {
          handleApplyFilters();
          setMobileSheet(null);
        }}
      >
        {t('Apply filters', '필터 적용')}
      </BaseButton>
    </div>
  );

  const mobileCategorySheet = activeSheetItem ? (
    <FilterBottomSheet
      expandable
      fullWidth
      onClose={() => setMobileSheet(null)}
    >
      <div className='flex h-full min-h-0 w-full flex-col'>
        <p className='typo-title1 shrink-0 pb-4 text-text-primary'>
          {activeSheetItem.label}
        </p>

        <div className='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
          {renderOptionList(activeSheetItem)}
        </div>

        <div className='shrink-0 border-t border-grayscale-gray02 pt-4'>
          {mobileFooterButtons}
        </div>
      </div>
    </FilterBottomSheet>
  ) : null;

  const mobileAllFiltersSheet =
    mobileSheet === 'all' && typeof document !== 'undefined'
      ? createPortal(
          <div className='fixed inset-0 z-[1100] flex flex-col bg-grayscale-gray00 tabletV:hidden'>
            <div className='flex justify-between shrink-0 items-center border-b border-grayscale-gray02 px-4 py-3'>
              <CloseButton onClick={() => setMobileSheet(null)} />
              <p className='typo-b16Strong text-text-primary'>
                {t('Filters', '필터')}
              </p>
              <div className='size-6' />
            </div>

            <div className='flex-1 overflow-y-auto scrollbar-hide px-[30px] pb-8 pt-[30px]'>
              <div className='flex w-full flex-col items-start border-b border-solid border-grayscale-gray02 pb-4'>
                {e7SupportRow}
              </div>

              <div className='mt-10 w-full'>
                {filterItems.map(renderAccordion)}
              </div>
            </div>

            <div className='shrink-0 border-t border-grayscale-gray02 bg-grayscale-gray00 p-4 pb-[calc(env(safe-area-inset-bottom)+16px)]'>
              {mobileFooterButtons}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {!collapsible && mobileChipBar}
      {mobileAllFiltersSheet}
      {mobileCategorySheet}

      <div
        className={`${collapsible ? 'flex w-full' : 'bg-grayscale-gray00 hidden tabletV:flex p-6 rounded-[30px] min-w-[273px] max-w-[273px]'} flex-col items-start overflow-clip relative ${className}`}
      >
        {collapsible ? (
          <button type='button' aria-expanded={expanded} aria-controls='job-management-filters' onClick={() => setExpanded((value) => !value)} className='flex w-full items-center justify-between p-4 min-h-[56px] typo-b16Strong text-text-primary'>
            <span>{t('Filters', '필터')}{totalAppliedCount > 0 ? ` (${totalAppliedCount})` : ''}</span>
            {expanded ? <ArrowTopIcon /> : <ArrowBottomIcon />}
          </button>
        ) : (
        <div className='border-b border-grayscale-gray02 flex items-start pb-6 relative shrink-0 w-full'>
          <p className='typo-title3 relative shrink-0 text-grayscale-gray09'>
            {t('Filters', '필터')}
          </p>
        </div>
        )}

        <div id={collapsible ? 'job-management-filters' : undefined} hidden={collapsible && !expanded} className={`w-full ${collapsible ? 'px-4 pb-4' : ''}`}>

        <div className='border-b border-grayscale-gray02 border-solid flex flex-col items-start px-0 py-4 relative shrink-0 w-full'>
          {e7SupportRow}
        </div>

        <div className='flex flex-col items-start pb-10 pt-0 px-0 relative shrink-0 w-full'>
          {filterItems.map(renderAccordion)}
        </div>

        <div className='flex flex-col gap-2 items-start relative shrink-0 w-full'>
          {actionButtons}
        </div>

        {!isAdmin && !collapsible && (
          <div className='absolute content-stretch flex flex-col items-center left-3 top-2 z-10'>
            <ToolTip
              label='Turn this on to see only jobs
that support E-7 visa'
              labelKr={'E-7 비자를 지원하는 공고만 보려면\n이 옵션을 켜세요'}
              position='bottom'
              align='center'
              state='accent'
              line='two'
              hideClose={false}
              className='w-[250px]'
            />
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;
